import axios from 'axios'
import { getCookie, removeCookie } from '@/assets/js/cookie'
import { AUTH_TOKEN_FIELD, isStaleAuthFailure } from '@/assets/js/authSession'

const ticketing = axios.create({
    baseURL: '/ticketing',
    timeout: 10000,
})

ticketing.interceptors.request.use((config) => {
    const token = getCookie('token')
    // Scanner calls authenticate with the verifier link alone; they must not carry
    // (or, on a 401, clear) the organiser's login in the same browser.
    config.isVerifierCall = !!(config.headers && config.headers['X-Verifier-Token'])
    if (token && !config.isVerifierCall) {
        config.headers['Authorization'] = `Bearer ${token}`
    }
    // Remember which token went out, so a late 401 can be matched against the
    // token we currently hold (see authSession.js).
    config[AUTH_TOKEN_FIELD] = token || ''
    return config
})

ticketing.interceptors.response.use(
    (res) => res.data,
    (err) => {
        // The ticketing API returns 401 once the Sa-Token session has expired.
        // Mirror store.apiResult: drop the dead token and prompt the user to log
        // back in, instead of surfacing a raw error — but skip that when the 401
        // belongs to a token that has already been replaced by a fresh login,
        // otherwise we delete the new token along with the dead one.
        const tokenUsed = err && err.config ? err.config[AUTH_TOKEN_FIELD] : ''
        if (err && err.response && err.response.status === 401 && !(err.config && err.config.isVerifierCall) && !isStaleAuthFailure(tokenUsed)) {
            removeCookie('token')
            // Lazy require to avoid a circular import with the store.
            try {
                require('@/store/store').default.commit('loginShow', true)
            } catch (e) {
                // store not ready — nothing more we can do here
            }
        }
        return Promise.reject(err)
    }
)

export { ticketing }

export default {
    listEvents() {
        return ticketing.get('/event')
    },
    getEvent(id) {
        return ticketing.get(`/event/${id}`)
    },
    createOrder(data) {
        return ticketing.post('/order', data)
    },
    getOrder(id) {
        return ticketing.get(`/order/${id}`)
    },

    // Tickets
    getMyTickets() {
        return ticketing.get('/tickets/mine')
    },
    generateQr(ticketId) {
        return ticketing.get(`/tickets/${ticketId}/qr`)
    },

    // Gifting
    listMyGifts() {
        return ticketing.get('/gifts/mine')
    },
    getGiftClaim(token) {
        return ticketing.get(`/gifts/claim/${token}`)
    },
    claimGift(payload) {
        // payload: { token, walletAddress? }
        return ticketing.post('/gifts/claim', payload)
    },

    // Check-in (verifier device)
    checkin(qrCode, verifierTokenHex) {
        return ticketing.post('/checkin', { qrCode }, {
            headers: { 'X-Verifier-Token': verifierTokenHex },
        })
    },
    checkout(qrCode, verifierTokenHex) {
        return ticketing.post('/checkout', { qrCode }, {
            headers: { 'X-Verifier-Token': verifierTokenHex },
        })
    },
    getVerifierSession(verifierTokenHex) {
        return ticketing.get('/checkin/session', {
            headers: { 'X-Verifier-Token': verifierTokenHex },
        })
    },
    getCheckinHistory(verifierTokenHex) {
        return ticketing.get('/checkin/history', {
            headers: { 'X-Verifier-Token': verifierTokenHex },
        })
    },
    getTicketHistory(qrCode, verifierTokenHex) {
        return ticketing.get(`/tickets/by-code/${qrCode}`, {
            headers: { 'X-Verifier-Token': verifierTokenHex },
        })
    },
    manualExit(qrCode, verifierTokenHex) {
        return ticketing.post(`/tickets/by-code/${qrCode}/manual-exit`, {}, {
            headers: { 'X-Verifier-Token': verifierTokenHex },
        })
    },
}
